/**
 * AI CONTEXT
 * Purpose: per-class promotion card in EvaluationView (inline skill, reasons, submit/approve, assignment).
 * Handles: minimal evaluation docs, promotion saves, assign-all actions.
 * Depends on: EvaluationViewContext data maps, promotionRules, PromotionRow.
 * Used by: EvaluationView masonry grid.
 * Refactor: move promotion mutations to a dedicated action hook to simplify component.
 */
import { App, Button, Select, Typography } from "antd"
import { deleteField } from "firebase/firestore"
import { createContext, useContext, useMemo, useRef, useState } from "react"

import Card from "#blocks/Card"
import Flex from "#blocks/Flex"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import List from "#blocks/List"
import Firebase from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import ClickableClassName from "#components/ClickableClassName"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  type AllSkillLevels,
  CLASS_GROUP_TO_SKILL_TYPE,
} from "#constants/skillLevelConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  EVAL_EVENT_TYPE,
  type EvalEventType,
} from "#customTypes/composedTypes/evalCommentTypes"
import {
  CreateEvaluationData,
  EVALUATION_STATUS,
  type NoPromoReason,
} from "#customTypes/composedTypes/evaluationTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import { Uuid } from "#customTypes/globalTypes"
import { GenericObject } from "#customTypes/types"
import { useSaveDoc } from "#hooks/jotai/useSaveDoc"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import useAnalytics from "#hooks/useAnalytics"
import useErrorModal from "#hooks/useErrorModal"
import { generateEvalId, getNextSkill } from "#utils/evaluationHelpers"
import { filterFormOptions } from "#utils/formHelpers"
import {
  buildNextSemesterSkillPatch,
  buildNoEvalSkillPatch,
  canSubmitPromotion,
  isPromoted,
  ladderFor,
  promotionNeedsApprove,
  promotionNeedsSubmit,
  skillPatchTouchesStudent,
} from "#utils/promotionRules"
import { buildSkillHistoryUpdate } from "#utils/studentSkillHelpers"

import {
  buildStudentRows,
  canApprovePromotion,
  canEditPromotion,
  type ClassStudent,
  getCurrentSkillForStudent,
  getSortedStudentIds,
  getVisibleStudentIds,
} from "./evaluationClassCardHelpers"
import PromotionRow, { PROMOTION_ROW_WIDTH } from "./PromotionRow"
import useEvaluationViewContext from "./useEvaluationViewContext"

/** Card padding (24px each side) plus the 1px border, in border-box. */
const CARD_HORIZONTAL_CHROME = 50

export const EVALUATION_CARD_WIDTH =
  PROMOTION_ROW_WIDTH + CARD_HORIZONTAL_CHROME

const { Text } = Typography

type MinimalEvalShell = CreateEvaluationData & {
  currentSkill: AllSkillLevels
  nextSkill: AllSkillLevels
  didWell: string
  needsWork: string
  checkboxes: Record<string, boolean>
}

const inProgressStampClear = () => ({
  status: EVALUATION_STATUS.IN_PROGRESS,
  submittedBy: deleteField(),
  submittedAt: deleteField(),
  approvedBy: deleteField(),
  approvedAt: deleteField(),
  rejectedBy: deleteField(),
  rejectedAt: deleteField(),
})

const needsStampClear = (status: EVALUATION_STATUS | undefined) =>
  status === EVALUATION_STATUS.APPROVED ||
  status === EVALUATION_STATUS.REJECTED ||
  status === EVALUATION_STATUS.SUBMITTED

interface ClassCardContextType {
  classGroup: CLASS_GROUPS
  semester: string
  teacherOptions: DropdownOption[]
  approverOptions: DropdownOption[]
  onAssignTeacher: (studentId: Uuid, teacherId: string | null) => Promise<void>
  onAssignApprover: (
    studentId: Uuid,
    approverId: string | null
  ) => Promise<void>
  onCurrentSkill: (studentId: Uuid, skill: string) => Promise<void>
  onNextSkill: (studentId: Uuid, skill: string) => Promise<void>
  onToggleReason: (
    studentId: Uuid,
    reason: NoPromoReason,
    checked: boolean
  ) => Promise<void>
  onNoteEditing: (studentId: Uuid) => Promise<void>
  onNote: (studentId: Uuid, text: string) => Promise<void>
  onSubmit: (studentId: Uuid) => Promise<void>
  onApprove: (studentId: Uuid) => Promise<void>
  onReject: (studentId: Uuid) => Promise<void>
  onNoEval: (studentId: Uuid, checked: boolean) => Promise<void>
}

const noopAsync = async () => {}

const ClassCardContext = createContext<ClassCardContextType>({
  classGroup: CLASS_GROUPS.CORE,
  semester: "",
  teacherOptions: [],
  approverOptions: [],
  onAssignTeacher: noopAsync,
  onAssignApprover: noopAsync,
  onCurrentSkill: noopAsync,
  onNextSkill: noopAsync,
  onToggleReason: noopAsync,
  onNoteEditing: noopAsync,
  onNote: noopAsync,
  onSubmit: noopAsync,
  onApprove: noopAsync,
  onReject: noopAsync,
  onNoEval: noopAsync,
})

const ClassStudentRow = ({
  item: { studentId, evaluationId },
}: {
  item: ClassStudent
}) => {
  const {
    classGroup,
    semester,
    teacherOptions,
    approverOptions,
    onAssignTeacher,
    onAssignApprover,
    onCurrentSkill,
    onNextSkill,
    onToggleReason,
    onNoteEditing,
    onNote,
    onSubmit,
    onApprove,
    onReject,
    onNoEval,
  } = useContext(ClassCardContext)

  const { evaluationsById, studentsById } = useEvaluationViewContext(
    (state) => ({
      evaluationsById: state.evaluationsById,
      studentsById: state.studentsById,
    })
  )

  const { currentUserId } = useUserContext()
  const canAssignTeacher = useHasPermission(
    RESOURCES.EVALUATION,
    USER_ACTIONS.ASSIGN_TEACHER_TO_EVAL
  )
  const canUpdateStudent = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.UPDATE
  )

  const student = studentsById[studentId]
  const evaluation = evaluationsById[evaluationId]
  const studentSkill = getCurrentSkillForStudent(student, classGroup, semester)
  const currentSkill = evaluation?.currentSkill || studentSkill
  const canEdit = canEditPromotion(
    currentUserId,
    evaluation?.assignedTo,
    canAssignTeacher
  )
  const canApprove = canApprovePromotion(
    currentUserId,
    evaluation?.approverId,
    canAssignTeacher
  )
  const approveNeedsStudentWrite = Boolean(
    evaluation?.nextSkill &&
      skillPatchTouchesStudent(
        buildNextSemesterSkillPatch({
          semester,
          classGroup,
          nextSkill: evaluation.nextSkill,
          evalId: evaluationId,
        })
      )
  )
  const noEvalNeedsStudentDelete = skillPatchTouchesStudent(
    buildNoEvalSkillPatch({
      skillHistory: student?.skillHistory,
      semester,
      classGroup,
      evalId: evaluationId,
    })
  )

  return (
    <PromotionRow
      student={student}
      classGroup={classGroup}
      evaluation={evaluation}
      currentSkill={currentSkill}
      canEdit={canEdit}
      canApprove={canApprove}
      canAssign={canAssignTeacher}
      canUpdateStudent={canUpdateStudent}
      approveNeedsStudentWrite={approveNeedsStudentWrite}
      noEvalNeedsStudentDelete={noEvalNeedsStudentDelete}
      teacherOptions={teacherOptions}
      approverOptions={approverOptions}
      onCurrentSkill={(skill) => {
        void onCurrentSkill(studentId, skill)
      }}
      onNextSkill={(skill) => {
        void onNextSkill(studentId, skill)
      }}
      onToggleReason={(reason, checked) => {
        void onToggleReason(studentId, reason, checked)
      }}
      onNoteEditing={() => {
        void onNoteEditing(studentId)
      }}
      onNote={(text) => {
        void onNote(studentId, text)
      }}
      onSubmit={() => {
        void onSubmit(studentId)
      }}
      onApprove={() => {
        void onApprove(studentId)
      }}
      onReject={() => {
        void onReject(studentId)
      }}
      onNoEval={(checked) => {
        void onNoEval(studentId, checked)
      }}
      onAssignTeacher={(teacherId) => {
        void onAssignTeacher(studentId, teacherId)
      }}
      onAssignApprover={(approverId) => {
        void onAssignApprover(studentId, approverId)
      }}
    />
  )
}

const EvaluationClassCard = ({
  item: classObj,
}: {
  item: ClassConstructor
}) => {
  const [selectedAssignAllTeacherId, setSelectedAssignAllTeacherId] =
    useState<Uuid | null>(null)
  const [selectedAssignAllApproverId, setSelectedAssignAllApproverId] =
    useState<Uuid | null>(null)
  const [isAssignAllLoading, setIsAssignAllLoading] = useState(false)
  const [isAssignAllApproverLoading, setIsAssignAllApproverLoading] =
    useState(false)
  const evalCreatePromises = useRef(new Map<Uuid, Promise<Uuid | null>>())

  const {
    usersById,
    evaluationsById,
    studentsById,
    teacherOptions,
    approverOptions,
    filteredStudentIdsByClassId,
    sortByStatus,
    searchTerm,
  } = useEvaluationViewContext((state) => {
    return {
      usersById: state.usersById,
      evaluationsById: state.evaluationsById,
      studentsById: state.studentsById,
      teacherOptions: state.teacherOptions,
      approverOptions: state.approverOptions,
      filteredStudentIdsByClassId: state.filteredStudentIdsByClassId,
      sortByStatus: state.sortByStatus,
      searchTerm: state.searchTerm,
    }
  })

  const sortedStudentIds = useMemo(
    () =>
      getSortedStudentIds(
        classObj.studentIds || [],
        evaluationsById,
        studentsById,
        classObj.semester,
        classObj.group,
        sortByStatus
      ),
    [classObj, studentsById, evaluationsById, sortByStatus]
  )

  const filteredStudentIds = filteredStudentIdsByClassId[classObj.id]
  const hasActiveFilters = !!filteredStudentIds

  const visibleStudentIds = useMemo(
    () =>
      getVisibleStudentIds(
        sortedStudentIds,
        hasActiveFilters ? filteredStudentIds : undefined,
        searchTerm,
        classObj.name,
        classObj.teacherIds || [],
        usersById,
        studentsById
      ),
    [
      sortedStudentIds,
      filteredStudentIds,
      hasActiveFilters,
      searchTerm,
      studentsById,
      classObj,
      usersById,
    ]
  )

  const studentRows: ClassStudent[] = useMemo(
    () =>
      buildStudentRows(
        visibleStudentIds,
        classObj.id,
        classObj.semester,
        classObj.group
      ),
    [visibleStudentIds, classObj]
  )

  const { currentUserId } = useUserContext()
  const canAssignTeacher = useHasPermission(
    RESOURCES.EVALUATION,
    USER_ACTIONS.ASSIGN_TEACHER_TO_EVAL
  )
  const canUpdateStudent = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.UPDATE
  )
  const { trackEvaluationAction } = useAnalytics()
  const { message } = App.useApp()

  const { saveDoc: createEvaluation } = useSaveDoc<MinimalEvalShell>(
    DOC_TYPES.EVALUATIONS
  )

  const openErrorModal = useErrorModal("saving an evaluation")

  const evalIdFor = (studentId: Uuid) =>
    generateEvalId(studentId, classObj.semester, classObj.group)

  const canEditStudent = (studentId: Uuid) =>
    canEditPromotion(
      currentUserId,
      evaluationsById[evalIdFor(studentId)]?.assignedTo,
      canAssignTeacher
    )

  const canApproveStudent = (studentId: Uuid) =>
    canApprovePromotion(
      currentUserId,
      evaluationsById[evalIdFor(studentId)]?.approverId,
      canAssignTeacher
    )

  const studentLabel = (studentId: Uuid) =>
    studentsById[studentId]?.name ?? `Unknown (${studentId})`

  const currentSkillFor = (studentId: Uuid): AllSkillLevels | "" => {
    const evaluation = evaluationsById[evalIdFor(studentId)]
    return (
      evaluation?.currentSkill ||
      getCurrentSkillForStudent(
        studentsById[studentId],
        classObj.group,
        classObj.semester
      )
    )
  }

  const tellSetCurrentLevel = (studentId: Uuid) => {
    void message.error(
      `Set a current level first for ${studentLabel(studentId)}.`
    )
  }

  const reportWriteError = (error: unknown, context: string) => {
    const typedError = error instanceof Error ? error : new Error(String(error))
    openErrorModal(typedError, context)
  }

  const errorContext = (studentId: Uuid, action: string) =>
    `${action}:
student:${studentLabel(studentId)} (${studentId})
class:${classObj.name} (${classObj.id})
evalId:${evalIdFor(studentId)}`

  const writeEval = async (
    evaluationId: Uuid,
    patch: GenericObject,
    event?: { eventType: EvalEventType; message?: string }
  ) => {
    await Firebase.getInstance().updateEvaluationWithEvent({
      evaluationId,
      patch,
      event:
        event && currentUserId
          ? {
              authorId: currentUserId,
              eventType: event.eventType,
              message: event.message,
            }
          : undefined,
    })
  }

  const writeStudentFields = async (studentId: Uuid, fields: GenericObject) => {
    await Firebase.getInstance().updateDocuments({
      [DOC_TYPES.STUDENTS]: { [studentId]: fields },
    })
  }

  const ensureEval = (
    studentId: Uuid,
    currentSkill: AllSkillLevels,
    assignedTo?: Uuid
  ): Promise<Uuid | null> => {
    const evalId = evalIdFor(studentId)
    if (evaluationsById[evalId]) return Promise.resolve(evalId)

    const inFlight = evalCreatePromises.current.get(evalId)
    if (inFlight) return inFlight

    const nextSkill = getNextSkill(currentSkill, classObj.group)
    const pending = createEvaluation({
      id: evalId,
      studentId,
      classGroup: classObj.group,
      classId: classObj.id,
      status: EVALUATION_STATUS.NOT_STARTED,
      semester: classObj.semester,
      curriculumId: classObj.curriculumId,
      currentSkill,
      nextSkill,
      didWell: "",
      needsWork: "",
      checkboxes: {},
      ...(assignedTo
        ? {
            assignedTo,
            assignedBy: currentUserId,
            assignedAt: new Date().toISOString(),
          }
        : {}),
    }).then((createdId) => {
      if (!createdId) evalCreatePromises.current.delete(evalId)
      return createdId
    })
    evalCreatePromises.current.set(evalId, pending)
    return pending
  }

  const handleAssignTeacher = async (
    studentId: Uuid,
    teacherId: string | null
  ) => {
    if (!canAssignTeacher) return
    const evalId = evalIdFor(studentId)
    const evaluation = evaluationsById[evalId]
    const assignmentPatch = {
      assignedTo: teacherId,
      assignedBy: currentUserId,
      assignedAt: new Date().toISOString(),
    }
    const assignmentEvent = {
      eventType: EVAL_EVENT_TYPE.AUTHOR_ASSIGNED,
      message: teacherId
        ? `${usersById[teacherId]?.name ?? `Unknown (${teacherId})`} assigned as new author`
        : "Author was removed",
    }

    try {
      if (evaluation) {
        if (teacherId) {
          trackEvaluationAction(ANALYTICS_EVENTS.EVALUATION_STARTED, {
            studentId,
            classId: classObj.id,
            classGroup: classObj.group,
          })
        }
        await writeEval(evaluation.id, assignmentPatch, assignmentEvent)
        return
      }

      if (!teacherId) return

      const currentSkill = getCurrentSkillForStudent(
        studentsById[studentId],
        classObj.group,
        classObj.semester
      )
      if (!currentSkill) {
        tellSetCurrentLevel(studentId)
        return
      }

      trackEvaluationAction(ANALYTICS_EVENTS.EVALUATION_STARTED, {
        studentId,
        classId: classObj.id,
        classGroup: classObj.group,
      })

      const joinedExistingCreate = evalCreatePromises.current.has(evalId)
      const createdId = await ensureEval(studentId, currentSkill, teacherId)
      if (!createdId) return
      if (joinedExistingCreate) {
        await writeEval(createdId, assignmentPatch, assignmentEvent)
      }
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Assigning a teacher"))
    }
  }

  const handleAssignAll = async (teacherId: Uuid | null) => {
    if (!canAssignTeacher) return
    setIsAssignAllLoading(true)
    if (!teacherId) {
      setIsAssignAllLoading(false)
      return
    }

    try {
      for (const studentId of sortedStudentIds) {
        await handleAssignTeacher(studentId, teacherId)
      }
    } finally {
      setIsAssignAllLoading(false)
    }
  }

  const handleAssignApprover = async (
    studentId: Uuid,
    approverId: string | null
  ) => {
    if (!canAssignTeacher) return
    const evaluation = evaluationsById[evalIdFor(studentId)]
    if (!evaluation) return

    try {
      await writeEval(
        evaluation.id,
        { approverId: approverId ?? null },
        {
          eventType: EVAL_EVENT_TYPE.APPROVER_ASSIGNED,
          message: approverId
            ? `${usersById[approverId]?.name ?? `Unknown (${approverId})`} assigned as new approver`
            : "Approver was removed",
        }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Assigning an approver"))
    }
  }

  const handleAssignAllApprovers = async (approverId: Uuid | null) => {
    if (!canAssignTeacher) return
    setIsAssignAllApproverLoading(true)
    if (!approverId) {
      setIsAssignAllApproverLoading(false)
      return
    }
    try {
      for (const studentId of sortedStudentIds) {
        await handleAssignApprover(studentId, approverId)
      }
    } finally {
      setIsAssignAllApproverLoading(false)
    }
  }

  const handleCurrentSkill = async (studentId: Uuid, skill: string) => {
    if (!canEditStudent(studentId) || !canUpdateStudent) return
    if (!ladderFor(classObj.group).includes(skill)) {
      tellSetCurrentLevel(studentId)
      return
    }
    const skillLevel = skill as AllSkillLevels
    const skillType = CLASS_GROUP_TO_SKILL_TYPE[classObj.group]

    try {
      const studentPatches: Record<Uuid, GenericObject> = {
        [studentId]: buildSkillHistoryUpdate(
          skillType,
          classObj.semester,
          skillLevel
        ),
      }
      await Firebase.getInstance().updateDocuments({
        [DOC_TYPES.STUDENTS]: studentPatches,
      })

      const evalId = await ensureEval(studentId, skillLevel)
      if (!evalId) return

      await writeEval(
        evalId,
        {
          ...inProgressStampClear(),
          currentSkill: skillLevel,
          nextSkill: getNextSkill(skillLevel, classObj.group),
          noPromoReason: null,
          noPromoText: null,
        },
        { eventType: EVAL_EVENT_TYPE.SAVED_DRAFT }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Saving current level"))
    }
  }

  const handleNextSkill = async (studentId: Uuid, nextSkill: string) => {
    if (!canEditStudent(studentId)) return
    const currentSkill = currentSkillFor(studentId)
    if (!currentSkill) {
      tellSetCurrentLevel(studentId)
      return
    }

    try {
      const evalId = await ensureEval(studentId, currentSkill)
      if (!evalId) return

      await writeEval(
        evalId,
        {
          ...inProgressStampClear(),
          nextSkill,
          ...(isPromoted(currentSkill, nextSkill, classObj.group)
            ? { noPromoReason: null, noPromoText: null }
            : {}),
        },
        { eventType: EVAL_EVENT_TYPE.SAVED_DRAFT }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Saving next level"))
    }
  }

  const handleToggleReason = async (
    studentId: Uuid,
    reason: NoPromoReason,
    checked: boolean
  ) => {
    if (!canEditStudent(studentId)) return
    const currentSkill = currentSkillFor(studentId)
    if (!currentSkill) {
      tellSetCurrentLevel(studentId)
      return
    }

    const evaluation = evaluationsById[evalIdFor(studentId)]
    const existing = evaluation?.noPromoReason ?? []
    const noPromoReason = checked
      ? existing.includes(reason)
        ? existing
        : [...existing, reason]
      : existing.filter((item) => item !== reason)

    try {
      const evalId = await ensureEval(studentId, currentSkill)
      if (!evalId) return
      await writeEval(
        evalId,
        {
          ...inProgressStampClear(),
          noPromoReason,
        },
        { eventType: EVAL_EVENT_TYPE.SAVED_DRAFT }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Saving a hold reason"))
    }
  }

  const handleNoteEditing = async (studentId: Uuid) => {
    if (!canEditStudent(studentId)) return
    const evaluation = evaluationsById[evalIdFor(studentId)]
    if (!evaluation || !needsStampClear(evaluation.status)) return

    try {
      await writeEval(evaluation.id, inProgressStampClear())
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Editing a hold note"))
    }
  }

  const handleNote = async (studentId: Uuid, text: string) => {
    if (!canEditStudent(studentId)) return
    const currentSkill = currentSkillFor(studentId)
    if (!currentSkill) {
      tellSetCurrentLevel(studentId)
      return
    }

    const evaluation = evaluationsById[evalIdFor(studentId)]
    try {
      const evalId = await ensureEval(studentId, currentSkill)
      if (!evalId) return
      await writeEval(
        evalId,
        {
          ...(needsStampClear(evaluation?.status)
            ? inProgressStampClear()
            : {}),
          noPromoText: text,
        },
        { eventType: EVAL_EVENT_TYPE.SAVED_DRAFT }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Saving a hold note"))
    }
  }

  const handleSubmit = async (studentId: Uuid) => {
    if (!canEditStudent(studentId)) return
    const evaluation = evaluationsById[evalIdFor(studentId)]
    if (!promotionNeedsSubmit(evaluation?.status)) return
    const currentSkill = currentSkillFor(studentId)
    if (
      !canSubmitPromotion({
        currentSkill,
        nextSkill: evaluation?.nextSkill,
        classGroup: classObj.group,
        status: evaluation?.status,
        noPromoReason: evaluation?.noPromoReason,
      })
    ) {
      return
    }

    if (!currentSkill) return

    try {
      const evalId = evaluation
        ? evaluation.id
        : await ensureEval(studentId, currentSkill)
      if (!evalId) return
      await writeEval(
        evalId,
        {
          status: EVALUATION_STATUS.SUBMITTED,
          submittedBy: currentUserId,
          submittedAt: new Date().toISOString(),
          rejectedBy: deleteField(),
          rejectedAt: deleteField(),
          approvedBy: deleteField(),
          approvedAt: deleteField(),
        },
        { eventType: EVAL_EVENT_TYPE.SUBMITTED }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Submitting a promotion"))
    }
  }

  const handleApprove = async (studentId: Uuid) => {
    if (!canApproveStudent(studentId)) return
    const evaluation = evaluationsById[evalIdFor(studentId)]
    if (!promotionNeedsApprove(evaluation?.status)) return
    const nextSkill = evaluation?.nextSkill
    if (!evaluation || !nextSkill) {
      void message.error(
        `Set a next level before approving ${studentLabel(studentId)}.`
      )
      return
    }

    const skillPatch = buildNextSemesterSkillPatch({
      semester: classObj.semester,
      classGroup: classObj.group,
      nextSkill,
      evalId: evaluation.id,
    })
    if (skillPatchTouchesStudent(skillPatch) && !canUpdateStudent) {
      void message.error(
        `You need permission to update ${studentLabel(studentId)}'s level before approving.`
      )
      return
    }

    try {
      if ("writes" in skillPatch) {
        await writeStudentFields(studentId, skillPatch.writes)
      }
      await writeEval(
        evaluation.id,
        {
          status: EVALUATION_STATUS.APPROVED,
          approvedBy: currentUserId,
          approvedAt: new Date().toISOString(),
          rejectedBy: deleteField(),
          rejectedAt: deleteField(),
        },
        { eventType: EVAL_EVENT_TYPE.APPROVED }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Approving a promotion"))
    }
  }

  const handleReject = async (studentId: Uuid) => {
    if (!canApproveStudent(studentId)) return
    const evaluation = evaluationsById[evalIdFor(studentId)]
    if (!evaluation || !promotionNeedsApprove(evaluation.status)) return

    try {
      await writeEval(
        evaluation.id,
        {
          status: EVALUATION_STATUS.REJECTED,
          rejectedBy: currentUserId,
          rejectedAt: new Date().toISOString(),
          approvedBy: deleteField(),
          approvedAt: deleteField(),
        },
        { eventType: EVAL_EVENT_TYPE.REJECTED }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Rejecting a promotion"))
    }
  }

  const handleNoEval = async (studentId: Uuid, checked: boolean) => {
    if (!canEditStudent(studentId)) return
    const evaluation = evaluationsById[evalIdFor(studentId)]

    if (!checked) {
      if (!evaluation) return
      try {
        await writeEval(evaluation.id, inProgressStampClear())
      } catch (error) {
        reportWriteError(error, errorContext(studentId, "Clearing no eval"))
      }
      return
    }

    const currentSkill = currentSkillFor(studentId)
    if (!currentSkill) {
      tellSetCurrentLevel(studentId)
      return
    }

    const evalId = evalIdFor(studentId)
    const skillPatch = buildNoEvalSkillPatch({
      skillHistory: studentsById[studentId]?.skillHistory,
      semester: classObj.semester,
      classGroup: classObj.group,
      evalId,
    })
    if (skillPatchTouchesStudent(skillPatch) && !canUpdateStudent) {
      void message.error(
        `You need permission to update ${studentLabel(studentId)}'s level before marking no eval.`
      )
      return
    }

    try {
      if ("deletes" in skillPatch) {
        const fields: GenericObject = {}
        for (const path of skillPatch.deletes) {
          fields[path] = deleteField()
        }
        await writeStudentFields(studentId, fields)
      }
      const id = evaluation
        ? evaluation.id
        : await ensureEval(studentId, currentSkill)
      if (!id) return
      await writeEval(
        id,
        {
          status: EVALUATION_STATUS.NO_EVAL,
          nextSkill: currentSkill,
          noPromoReason: null,
          noPromoText: null,
        },
        { eventType: EVAL_EVENT_TYPE.SAVED_DRAFT }
      )
    } catch (error) {
      reportWriteError(error, errorContext(studentId, "Marking no eval"))
    }
  }

  const hasAnyEvaluations = studentRows.some(
    ({ evaluationId }) => !!evaluationsById[evaluationId]
  )

  if (!studentRows.length)
    return (
      <Flex col>
        <Text strong>{classObj.name}</Text>
        <Text>No students in this class</Text>
      </Flex>
    )

  if (classObj.name.includes("TA"))
    return (
      <Flex col>
        <Text strong>{classObj.name}</Text>
        <Text>TA class</Text>
      </Flex>
    )

  return (
    <Card
      style={{
        width: EVALUATION_CARD_WIDTH,
        marginBottom: 30,
      }}
    >
      <Flex col gap={8}>
        <Flex gap={12} style={{ alignItems: "center", marginBottom: "8px" }}>
          <Text strong style={{ fontSize: "18px" }}>
            <ClickableClassName classId={classObj.id} label={classObj.name} />
          </Text>
          <Flex gap={8} style={{ marginLeft: "auto" }}>
            <DisabledFieldTooltip
              disabled={!canAssignTeacher || !selectedAssignAllTeacherId}
              tooltip={
                canAssignTeacher
                  ? "Select a teacher from the list"
                  : "You need permission to assign teachers"
              }
            >
              <Button
                onClick={() => handleAssignAll(selectedAssignAllTeacherId)}
                disabled={!canAssignTeacher || !selectedAssignAllTeacherId}
                loading={isAssignAllLoading}
                size="small"
              >
                Assign all
              </Button>
            </DisabledFieldTooltip>
            <Select
              placeholder="Select a teacher"
              options={teacherOptions}
              style={{ width: 160 }}
              size="small"
              value={selectedAssignAllTeacherId}
              onChange={setSelectedAssignAllTeacherId}
              disabled={!canAssignTeacher}
              showSearch
              filterOption={filterFormOptions}
            />

            <DisabledFieldTooltip
              disabled={
                !canAssignTeacher ||
                !hasAnyEvaluations ||
                !selectedAssignAllApproverId
              }
              tooltip={
                !canAssignTeacher
                  ? "You need permission to assign approvers"
                  : !hasAnyEvaluations
                    ? "Assign teachers first to create evaluations"
                    : "Select an approver from the list"
              }
            >
              <Button
                onClick={() =>
                  handleAssignAllApprovers(selectedAssignAllApproverId)
                }
                disabled={
                  !canAssignTeacher ||
                  !selectedAssignAllApproverId ||
                  !hasAnyEvaluations
                }
                loading={isAssignAllApproverLoading}
                size="small"
              >
                Approver all
              </Button>
            </DisabledFieldTooltip>
            <DisabledFieldTooltip
              disabled={!canAssignTeacher || !hasAnyEvaluations}
              tooltip={
                canAssignTeacher
                  ? "Assign teachers first to create evaluations"
                  : "You need permission to assign approvers"
              }
            >
              <Select
                placeholder="Select approver"
                options={approverOptions}
                style={{ width: 160 }}
                size="small"
                value={selectedAssignAllApproverId}
                onChange={setSelectedAssignAllApproverId}
                disabled={!canAssignTeacher || !hasAnyEvaluations}
                showSearch
                filterOption={filterFormOptions}
              />
            </DisabledFieldTooltip>
          </Flex>
        </Flex>

        <ClassCardContext.Provider
          value={{
            classGroup: classObj.group,
            semester: classObj.semester,
            teacherOptions,
            approverOptions,
            onAssignTeacher: handleAssignTeacher,
            onAssignApprover: handleAssignApprover,
            onCurrentSkill: handleCurrentSkill,
            onNextSkill: handleNextSkill,
            onToggleReason: handleToggleReason,
            onNoteEditing: handleNoteEditing,
            onNote: handleNote,
            onSubmit: handleSubmit,
            onApprove: handleApprove,
            onReject: handleReject,
            onNoEval: handleNoEval,
          }}
        >
          <List
            items={studentRows}
            layout="vertical"
            style={{
              width: PROMOTION_ROW_WIDTH,
              paddingBottom: 0,
              overflow: "visible",
            }}
            gap={0}
            component={ClassStudentRow}
            disableLayoutAnimation
          />
        </ClassCardContext.Provider>
      </Flex>
    </Card>
  )
}

export default EvaluationClassCard
